// Aditude Cloud Wrapper (window.tude) adapter.
//
// Every ad call on the site goes through this file so the ad provider can be
// swapped in one place. The wrapper script itself is added to <head> in
// gatsby-ssr.js; ensureAdWrapperLoaded() covers client side navigations that
// start on a page where the script was intentionally left out.
//
// Integration guide: "MindBodyGreen Cloud Wrapper Documentation" from Aditude.
// Contact: integration@aditude.io

// Production uses the live wrapper; every other build (deploy previews,
// staging, local) uses Aditude's staging path. GATSBY_ADITUDE_SCRIPT_URL overrides both.
export const isProdSite = /www\.mindbodygreen\.com/.test(process.env.GATSBY_LOCAL_SITE || "")

export const ADITUDE_SCRIPT_URL =
  process.env.GATSBY_ADITUDE_SCRIPT_URL ||
  (isProdSite
    ? "https://htlbid.com/v3/mindbodygreen.com/htlbid.js"
    : "https://htlbid.com/stage/v3/mindbodygreen.com/htlbid.js")

// Base div ids from the "Sites & Mappings" section of the Aditude guide. Each
// one maps to a GAM ad unit, sizes and Prebid config inside the wrapper.
export const BASE_DIV_IDS = [
  "Any_Leaderboard",
  "Article_Right_Rail",
  "Desktop_300x250_300x600_1",
  "Desktop_300x250_300x600_2",
  "Desktop_300x250_300x600_3",
  "Desktop_300x250_300x600_4",
  "Desktop_300x250_300x600_5",
  "Desktop_300x250_300x600_6",
  "Desktop_Bottom_Strip",
  "Mobile_300x250_1",
  "Mobile_Adhesion",
  "Mobile_Leaderboard",
  "Mobile_Leaderboard_Bottom",
  "Native_ArticlePage",
  "Native_ArticlePage_2",
  "Native_ArticlePage_3",
  "Native_ArticlePage_4",
  "Native_Homepage_FOP_1",
  "Native_Homepage_FOP_2",
  "Native_Homepage_FOP_3",
  "Native_Homepage_FOP_4",
  "Native_Homepage_RightRail",
  "Native_Inline_Article",
  "Native_YMAL_Article",
  "Article_Breaker",
  "Breaker_Leaderboard",
  "Breaker_Leaderboard_2",
  "Breaker_Leaderboard_3",
  "HP_Leaderboard_1",
  "HP_Leaderboard_2",
  "HP_Leaderboard_3",
  "Article_Inline",
  "InLine_Article_1",
  "InLine_Article_2",
  "InLine_Article_3",
  "InLine_Article_4",
  "InLine_Article_5",
  "InLine_Article_6",
  "InLine_Article_7",
  "InLine_Article_8",
  "InLine_Article_9",
  "InLine_Article_10",
]

export const isBaseDivId = (unitName) => BASE_DIV_IDS.includes(unitName)

// The wrapper expects base div ids in class selector form, e.g. ".htlad-Any_Leaderboard"
const toBaseDivSelector = (unitName) => `.htlad-${unitName}`

// Pending Aditude confirmation that triggerNextPageEvents() is their equivalent
// of the old htlbid.newPage() for route changes and infinite scroll.
const TRIGGER_NEXT_PAGE_EVENTS = false

const getTude = () => {
  window.tude = window.tude || { cmd: [] }
  window.tude.cmd = window.tude.cmd || []
  return window.tude
}

export const pushTudeCmd = (fn) => {
  if (typeof window === "undefined") return
  getTude().cmd.push(() => {
    try {
      fn(window.tude)
    } catch (error) {
      console.error("Aditude command failed:", error)
    }
  })
}

export const ensureAdWrapperLoaded = () => {
  if (typeof window === "undefined") return
  if (document.querySelector(`script[src="${ADITUDE_SCRIPT_URL}"]`)) return

  getTude()
  const script = document.createElement("script")
  script.async = true
  script.src = ADITUDE_SCRIPT_URL
  document.head.appendChild(script)
}

// GAM key-values must be strings or arrays of strings
const normalizeTargeting = (targeting = {}) =>
  Object.entries(targeting).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc
    acc[key] = Array.isArray(value) ? value.map(String) : String(value)
    return acc
  }, {})

export const displayAd = ({ divId, unitName, targeting }) => {
  ensureAdWrapperLoaded()
  pushTudeCmd((tude) => {
    tude.refreshAdsViaDivMappings([
      {
        divId,
        baseDivId: toBaseDivSelector(unitName),
        targeting: normalizeTargeting(targeting),
      },
    ])
  })
}

// Page level key-values, e.g. nsfw on the current article. Values set here
// persist until overwritten, so callers must reset them when they no longer apply.
export const setAdPageTargeting = (targeting) => {
  pushTudeCmd((tude) => tude.setPageTargeting(normalizeTargeting(targeting)))
}

export const destroyAd = (divId) => {
  pushTudeCmd((tude) => tude.destroyAds?.([divId]))
}

// Called on client side route changes and when infinite scroll moves to a new article
export const adsNextPage = () => {
  if (!TRIGGER_NEXT_PAGE_EVENTS) return
  pushTudeCmd((tude) => tude.triggerNextPageEvents?.())
}
